import get from 'lodash/get';
import groupBy from 'lodash/groupBy';
import keyBy from 'lodash/keyBy';
import partition from 'lodash/partition';

import { RESET_ENDPOINT_TYPE_CHANGED } from 'actions/flowBlockInput';
import { VALIDATE_SCRIPT_STEP_REMOVED } from 'actions/flowValidations';
import { SCRIPT_STEP_MODAL_SAVE_SUCCESS } from 'actions/scriptStepModal';
import { unpackPagedData } from 'reducers/utils';
import { AuthMethodTypes } from 'utils/authMethodTypes';
import { transformStepData, upsertById, makeFlowBlockInput } from './utils';

function updateCanvasForStepDelete(planCanvas, stepId) {
  // delete all blocks referencing the step and the edges referencing the deleted blocks
  const canvas = { ...planCanvas };
  const deletedBlocks = canvas.blocks
    .filter(b => b.step.id === stepId)
    .map(b => b.id);
  canvas.blocks = canvas.blocks.filter(b => b.step.id !== stepId);
  canvas.edges = canvas.edges.filter(
    e =>
      !deletedBlocks.includes(e.from.block.id) &&
      !deletedBlocks.includes(e.to.block.id),
  );
  return canvas;
}

function getScriptStepId(uiIntegrationsById, block) {
  const stepId = block?.step?.id;
  const integration = uiIntegrationsById[stepId];
  return integration?.scriptStep?.id ?? stepId;
}

function reconcileInputs({
  existingInputs,
  updatedStep,
  blocks,
  uiIntegrationsById,
}) {
  const scriptStepInputs = updatedStep?.inputs?.data ?? [];
  const scriptStepInputsMap = new Map(scriptStepInputs.map(i => [i.id, i]));
  const [affectedInputs, unaffectedInputs] = partition(
    existingInputs,
    input => get(input, 'scriptStepInput.scriptStep.id') === updatedStep.id,
  );

  const updatedInputs = affectedInputs
    // delete all block inputs that reference a deleted step input
    .filter(input => scriptStepInputsMap.has(input.scriptStepInput.id))
    // update block inputs from step input data that might have changed
    .map(input => ({
      ...input,
      scriptStepInput: scriptStepInputsMap.get(input.scriptStepInput.id),
    }));

  // for each new script step input, add a new input for each related block
  const updatedInputsByScriptStepInputId = groupBy(
    updatedInputs,
    'scriptStepInput.id',
  );
  const newScriptStepInputs = scriptStepInputs.filter(
    scriptStepInput => !updatedInputsByScriptStepInputId[scriptStepInput.id],
  );
  const blocksByScriptStepId = groupBy(blocks, block =>
    getScriptStepId(uiIntegrationsById, block),
  );
  const newInputs = newScriptStepInputs.flatMap(scriptStepInput => {
    const affectedBlocks =
      blocksByScriptStepId[scriptStepInput.scriptStep.id] || [];
    return affectedBlocks.map(block =>
      makeFlowBlockInput(block.id, scriptStepInput),
    );
  });

  return [...updatedInputs, ...newInputs, ...unaffectedInputs];
}

function getUpdatedRunEnv({ environment, agents }, allowedEnvironment) {
  return allowedEnvironment !== 'ANY' && allowedEnvironment !== environment
    ? {
        environment: allowedEnvironment,
        agents: [],
      }
    : { environment, agents };
}

function getUpdatedAuthType({ authenticationType }, triggerAuthType) {
  return triggerAuthType !== AuthMethodTypes.ALLOW_ALL &&
    authenticationType !== triggerAuthType
    ? { authenticationType: triggerAuthType }
    : { authenticationType };
}

function updateBlocksOnStepChange({
  existingBlocks,
  updatedStep,
  uiIntegrationsById,
}) {
  const [relatedBlocks, unrelatedBlocks] = partition(
    existingBlocks,
    block => getScriptStepId(uiIntegrationsById, block) === updatedStep.id,
  );
  const updatedBlocks = relatedBlocks.map(block => ({
    ...block,
    ...getUpdatedRunEnv(block, updatedStep.allowedEnvironment),
  }));

  return [...updatedBlocks, ...unrelatedBlocks];
}

function updateUIIntegrationsOnStepChange({ uiIntegrations, updatedStep }) {
  const [relatedUIIntegrations, unrelatedUIIntegrations] = partition(
    uiIntegrations,
    integration => integration?.scriptStep?.id === updatedStep.id,
  );
  const updatedUIIntegrations = relatedUIIntegrations.map(integration => ({
    ...integration,
    ...getUpdatedRunEnv(integration, updatedStep.allowedEnvironment),
    ...getUpdatedAuthType(integration, updatedStep.triggerAuthType),
  }));

  return [...updatedUIIntegrations, ...unrelatedUIIntegrations];
}

export function handleFlowChanges(state, action) {
  const { type, payload } = action;
  switch (type) {
    case SCRIPT_STEP_MODAL_SAVE_SUCCESS: {
      const { planCanvas } = state;
      const uiIntegrationsById = keyBy(state.uiIntegrations, 'id');
      const step = transformStepData(payload);
      const existingInputs = state.planCanvas?.inputs ?? [];
      const inputs = reconcileInputs({
        existingInputs,
        updatedStep: step,
        blocks: planCanvas.blocks,
        uiIntegrationsById,
      });
      const blocks = updateBlocksOnStepChange({
        existingBlocks: planCanvas.blocks,
        updatedStep: step,
        uiIntegrationsById,
      });

      const uiIntegrations = updateUIIntegrationsOnStepChange({
        uiIntegrations: state.uiIntegrations,
        updatedStep: step,
      });

      const prevStep = state.customSteps.find(
        customStep => customStep?.id === step?.id,
      );

      return {
        ...state,
        customSteps: upsertById(state.customSteps, step.id, step),
        inputs,
        blocks,
        uiIntegrations,
        planCanvas: {
          ...state.planCanvas,
          inputs,
          blocks,
        },
        isCanvasDirty: true,
        isEndpointTypeChanged: prevStep?.endpointType !== step?.endpointType,
      };
    }
    case RESET_ENDPOINT_TYPE_CHANGED: {
      return {
        ...state,
        isEndpointTypeChanged: false,
      };
    }
    case VALIDATE_SCRIPT_STEP_REMOVED: {
      return {
        ...state,
        planCanvas: updateCanvasForStepDelete(state.planCanvas, payload),
        customSteps: state.customSteps.filter(s => s.id !== payload),
      };
    }
    default:
      return state;
  }
}

function findScriptStepInput(customSteps, integrationSteps, scriptStepInputId) {
  return customSteps
    .flatMap(s => unpackPagedData(s.inputs))
    .filter(s => s)
    .concat(integrationSteps.flatMap(s => unpackPagedData(s.inputs)))
    .find(i => i.id === scriptStepInputId);
}

export function handleBlockInputChanges(state, payload) {
  const { flowBlockId, scriptStepInputId, value } = payload;
  const { planCanvas, customSteps, integrationSteps } = state;
  let inputs = planCanvas.inputs ? [...planCanvas.inputs] : [];
  const inputIndex = inputs.findIndex(
    i =>
      i.scriptStepInput &&
      i.flowBlock.id === flowBlockId &&
      i.scriptStepInput.id === scriptStepInputId,
  );

  let changed = null;
  if (inputIndex >= 0) {
    inputs[inputIndex] = {
      ...inputs[inputIndex],
      value,
    };
    changed = inputs[inputIndex];
  } else {
    const scriptStepInput = findScriptStepInput(
      customSteps,
      integrationSteps,
      scriptStepInputId,
    );
    changed = {
      flowBlock: { id: flowBlockId },
      scriptStepInput,
      value,
    };
    inputs = [...inputs, changed];
  }

  return {
    ...state,
    isCanvasDirty: true,
    planCanvas: { ...planCanvas, inputs },
    inputs,
  };
}

export function handleBlockEndpointChanges(state, payload) {
  const endpoints = state.endpoints ? [...state.endpoints] : [];
  const { planCanvas } = state;
  const { flowBlockId, endpoint } = payload;

  const prevIndex = endpoints.findIndex(e => e.flowBlockId === flowBlockId);
  const index = prevIndex === -1 ? endpoints.length : prevIndex;
  endpoints[index] = { flowBlockId, endpoint };

  const blocks = (planCanvas.blocks ?? []).map(block => {
    if (block.id === flowBlockId) {
      return { ...block, endpoint: { ...block.endpoint, id: endpoint.id } };
    }
    return block;
  });

  return {
    ...state,
    isCanvasDirty: true,
    isEndpointTypeChanged: false,
    planCanvas: { ...planCanvas, blocks },
    endpoints,
  };
}
